if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButtons = this.querySelectorAll('.product-form__submit[type="submit"]');
        this.addToCartButton = this.querySelector('[data-add-to-cart]') || this.submitButtons[0];
        this.buyNowButton = this.querySelector('[data-buy-now]');
        this.submitButton = this.addToCartButton;
        this.addToCartLabel =
          this.dataset.addToCartLabel || window.variantStrings?.addToCart || 'Add to cart';
        this.buyNowLabel = this.dataset.buyNowLabel || window.variantStrings?.buyNow || 'Buy now';

        if (document.querySelector('cart-drawer') && this.addToCartButton) {
          this.addToCartButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        const activeButton = evt.submitter || this.addToCartButton;
        if (!activeButton || activeButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.activeSubmitButton = activeButton;
        this.setSubmitButtonsLoading(true);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.activeSubmitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.setSubmitButtonsDisabled(true);
              this.activeSubmitButton.querySelector('span')?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            const buyNow = this.activeSubmitButton?.hasAttribute('data-buy-now');

            if (buyNow) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              this.redirectAfterAdd();
              return;
            }

            if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url || '/cart';
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.setSubmitButtonsLoading(false);
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.setSubmitButtonsDisabled(false);

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      setSubmitButtonsLoading(isLoading) {
        this.submitButtons.forEach((button) => {
          button.classList.toggle('loading', isLoading);
          button.toggleAttribute('aria-disabled', isLoading);
          button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
        });
      }

      setSubmitButtonsDisabled(isDisabled) {
        this.submitButtons.forEach((button) => {
          if (isDisabled) {
            button.setAttribute('aria-disabled', 'true');
          } else {
            button.removeAttribute('aria-disabled');
          }
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        this.submitButtons.forEach((button) => {
          const defaultLabel = button.dataset.addToCart
            ? this.addToCartLabel
            : button.hasAttribute('data-buy-now')
              ? this.buyNowLabel
              : button.querySelector('[data-default-label]')?.dataset.defaultLabel;
          const labelSpan = button.querySelector('[data-default-label]');
          const statusSpan = button.querySelector('.product-form__submit-status');

          if (disable) {
            button.setAttribute('disabled', 'disabled');
            if (text && labelSpan) {
              labelSpan.textContent = text;
              labelSpan.classList.remove('hidden');
            }
            if (statusSpan) {
              statusSpan.textContent = text || '';
              statusSpan.classList.toggle('hidden', !text);
            }
            if (button.hasAttribute('data-buy-now') && text) {
              labelSpan?.classList.add('hidden');
            }
          } else {
            button.removeAttribute('disabled');
            if (labelSpan && defaultLabel) {
              labelSpan.textContent = defaultLabel;
              labelSpan.classList.remove('hidden');
            }
            statusSpan?.classList.add('hidden');
          }
        });
      }

      redirectAfterAdd() {
        window.location = window.routes.checkout_url || '/checkout';
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
